// LoadingProgressBar.js — Externally driven loading screen
// Accepts phase + recipesReady from parent (MealPlan) to show real-time progress.
// Phases: "generating" → "streaming" → "grocery" → done (component unmounts)
import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react';
import { useTranslation } from 'react-i18next';

// Memoized Lottie wrapper — isolated from parent re-renders (message rotation,
// progress ref updates). Props are static (same src/ref callback identity),
// so this component renders exactly once for the lifetime of the loader.
//
// We use the Worker variant (OffscreenCanvas + Web Worker) so the Lottie
// render loop runs off the main thread. Progress bar rAF updates and text
// swaps on the main thread can no longer stall or drop Lottie frames.
const LottieAvocado = memo(function LottieAvocado({ dotLottieRefCallback }) {
  return (
    <DotLottieWorkerReact
      className="mx-auto"
      src="https://lottie.host/ab66b03d-6127-4dfc-a389-8021d354c8df/357358nYdS.lottie"
      loop
      autoplay
      width={600}
      height={600}
      dotLottieRefCallback={dotLottieRefCallback}
    />
  );
});

/**
 * @param {"generating"|"streaming"|"grocery"} phase
 *   - "generating" = waiting for first recipe from o3-mini
 *   - "streaming"  = recipes arriving 1/9 … 9/9
 *   - "grocery"    = grocery list being built
 * @param {number} recipesReady - how many recipes received so far (0-9)
 * @param {number} totalRecipes - expected total (default 9)
 */
const LoadingProgressBar = ({ phase = "generating", recipesReady = 0, totalRecipes = 9, isExiting = false, onLoopBoundary }) => {
  const { t } = useTranslation('mealplan');
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // ── Progress bar ────────────────────────────────────────────
  // Ranges: generating 0-65%, streaming 65-95%, grocery 95-100%
  // NOTE: we mutate the DOM node directly via ref to avoid a full React
  // re-render every animation frame (which would stall Lottie's render loop
  // and cause visible jank). We also animate `transform: scaleX()` instead
  // of `width` so the GPU compositor handles it without layout/paint.
  const progressBarRef = useRef(null);
  const displayProgressRef = useRef(0);
  const animFrameRef = useRef(null);
  const targetProgressRef = useRef(0);

  // Compute target based on external phase + recipesReady
  useEffect(() => {
    if (phase === "generating") {
      // Slow crawl from 0% → 20% driven by internal timer
      // (handled by the generating-phase timer below)
    } else if (phase === "streaming") {
      // 65% + (recipesReady / totalRecipes) * 30%  →  range 65%-95%
      targetProgressRef.current = 65 + (recipesReady / totalRecipes) * 30;
    } else if (phase === "grocery") {
      // Slowly crawl from 95% → 100% (internal timer), will jump to 100 on unmount
      targetProgressRef.current = 95;
    }
  }, [phase, recipesReady, totalRecipes]);

  // ── Generating phase: independent slow crawl 0 → 20% ───────
  const genIntervalRef = useRef(null);
  useEffect(() => {
    if (phase === "generating") {
      let p = 0;
      genIntervalRef.current = setInterval(() => {
        // Ease-out: fast at start, slower near 65%
        p = Math.min(65, p + (65 - p) * 0.015);
        targetProgressRef.current = p;
      }, 200);
    }
    return () => {
      if (genIntervalRef.current) clearInterval(genIntervalRef.current);
    };
  }, [phase]);

  // ── Grocery phase: slow crawl 95 → 100% ────────────────────
  const groceryIntervalRef = useRef(null);
  useEffect(() => {
    if (phase === "grocery") {
      let p = 95;
      groceryIntervalRef.current = setInterval(() => {
        p = Math.min(99, p + (99 - p) * 0.03);
        targetProgressRef.current = p;
      }, 250);
    }
    return () => {
      if (groceryIntervalRef.current) clearInterval(groceryIntervalRef.current);
    };
  }, [phase]);

  // ── Smooth animation loop: displayProgress → targetProgress ─
  // Writes directly to the DOM node's transform so React doesn't re-render
  // the tree (and especially the Lottie child) on every frame.
  useEffect(() => {
    let running = true;
    const animate = () => {
      if (!running) return;
      const target = targetProgressRef.current;
      const prev = displayProgressRef.current;
      const next = Math.abs(target - prev) < 0.1
        ? target
        : prev + (target - prev) * 0.12;
      if (next !== prev) {
        displayProgressRef.current = next;
        const node = progressBarRef.current;
        if (node) {
          node.style.transform = `scaleX(${next / 100})`;
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      running = false;
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // ── Message rotation ────────────────────────────────────────
  const loadingMessages = t('loadingMessages', { returnObjects: true }) || [];
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!loadingMessages.length) return;
    const iv = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 6000);
    return () => clearInterval(iv);
  }, [loadingMessages.length]);

  // ── Build display strings ───────────────────────────────────
  const getSubtitle = useCallback(() => {
    if (phase === "generating") {
      return t('loadingPhase.creating');
    }
    if (phase === "streaming") {
      return t('loadingPhase.loadingMeals', { current: recipesReady, total: totalRecipes });
    }
    if (phase === "grocery") {
      return t('loadingPhase.preparingGrocery');
    }
    return '';
  }, [phase, recipesReady, totalRecipes, t]);

  const message = (loadingMessages.length > 0)
    ? loadingMessages[messageIndex]
    : null;

  // Support both legacy string messages and new { q, a } objects
  const messageQ = message && typeof message === 'object' ? message.q : null;
  const messageA = message && typeof message === 'object'
    ? message.a
    : (typeof message === 'string' ? message : t('progressMessages.eat9Meals'));

  // ── DotLottie loop event → notify parent when safe to unmount ────
  // Defer setState to the next frame so the Lottie event handler returns
  // immediately and doesn't stall Lottie's internal render loop.
  const onLoopBoundaryRef = useRef(onLoopBoundary);
  useEffect(() => { onLoopBoundaryRef.current = onLoopBoundary; }, [onLoopBoundary]);
  const dotLottieRefCallback = useCallback((instance) => {
    if (!instance) return;
    const handler = () => {
      // Schedule on next macrotask so Lottie can finish its current frame first.
      setTimeout(() => {
        if (onLoopBoundaryRef.current) onLoopBoundaryRef.current();
      }, 0);
    };
    instance.addEventListener('loop', handler);
  }, []);

  return (
    <div
      className="text-center py-8 px-8 min-h-safe-screen flex flex-col items-center justify-center"
      style={{
        opacity: isExiting ? 0 : (hasEntered ? 1 : 0),
        transform: isExiting
          ? 'translate3d(0, -8px, 0)'
          : (hasEntered ? 'translate3d(0, 0, 0)' : 'translate3d(0, 12px, 0)'),
        transition: 'opacity 360ms cubic-bezier(0.22, 1, 0.36, 1), transform 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'opacity, transform',
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <div
        className="mx-auto items-center justify-center"
        style={{
          maxWidth: '300px',
          // Promote Lottie into its own compositor layer and isolate it
          // from layout/paint of sibling text changes.
          contain: 'layout paint style',
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
      >
        <LottieAvocado dotLottieRefCallback={dotLottieRefCallback} />
      </div>
      <div
        className="mb-3 min-h-[68px] max-w-md mx-auto"
        style={{ contain: 'layout paint' }}
      >
        {messageQ && (
          <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-semibold leading-[28px]">
            {messageQ}
          </p>
        )}
        <p className={`text-[var(--Color-Grays-Gray-8,#56554D)] text-[15.2px] font-normal leading-[24px] ${messageQ ? 'mt-1' : ''}`}>
          {messageA}
        </p>
      </div>
      <div className="w-full max-w-md mx-auto rounded h-1 overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
        <div
          ref={progressBarRef}
          className="h-full"
          style={{
            backgroundColor: '#0D0C07',
            transform: 'scaleX(0)',
            transformOrigin: 'left center',
            willChange: 'transform',
          }}
        ></div>
      </div>
      <p className="
              text-[var(--Color-Grays-Gray-10,#0D0C07)]
              text-[15.2px]
              font-normal
              leading-[28px]
              mt-3">{getSubtitle()}</p>
    </div>
  );
};

export default LoadingProgressBar;